import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Image, LabelFontFamily } from 'UI-Dash-Commons-UI';
import TopNavComponent from '../../Navigation/components/TopNavComponent';
import {
    DocsPageContent,
    DocsPageContentAnchor, DocsPageContentSpace,
    DocsPageContentText,
    DocsPageHeader,
    DocsPageHeaderType, IndentWidthType
} from '../../../common';

const logo = require( '../images/menu_full_logo.svg' );

const FAQComponent = () =>
{
    useEffect( () =>
    {
        document.title = "AppLovin SKA FAQ";
    }, [] );
    return <>
        <LeftSideMenuContainerWrapper>
            <Logo
                src={logo}
                alt={'AppLovin'}
                height={'28px'}
            />
        </LeftSideMenuContainerWrapper>
        <MainWrapper>
            <TopNavComponent/>
            <ContentWrapper>
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_1}
                    text={'iOS 14.5 Update: Apple’s Privacy Update & Your Advertising Campaigns'}
                />
                <DocsPageContent
                    text={
                        `Starting Monday, April 26, Apple's major iOS 14.5 update was released. 
                        All apps submitted to the App Store from an iPhone, iPad, or Apple Watch need to have 
                        App Tracking Transparency (ATT) implemented. According to Apple, any app that does not 
                        adhere to the new policy will be removed from the App Store, so staying ahead of the 
                        update is critical.`
                    }
                />
                <DocsPageContent
                    text={
                        `To help you better prepare and understand what to expect for your advertising 
                        campaigns we’ve created these Q&As:`
                    }
                />
                <DocsPageContent>
                    <DocsPageContentText text={ `If you’re a developer that uses MAX, check out our blog on ` } />
                    <DocsPageContentAnchor
                        text={`how to prepare for iOS 14.5`}
                        to={`https://www.applovin.com/blog/apple-ios-14-updates-for-max-developers/`}
                    />
                    <DocsPageContentText text={`.`}/>
                </DocsPageContent>

                <DocsPageContentSpace withSpace={IndentWidthType.SMALL}/>

                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_1}
                    text={'Changes to billing and attribution methods'}
                />
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: What changes to billing should I expect?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={` As of `} />
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `May 19th 2021`
                        }
                    />
                    <DocsPageContentText text={
                        `, we will begin to primarily use
                         SKAdNetwork reported attribution to bill for the currently estimated 90% of users that are eligible 
                         (i.e., those on iOS version >= 14.0 and served via AppLovin SDK version >= 6.13.3) on a CPI basis. 
                         Note you will be charged for both SKAdNetwork installs and redownloads. The remaining non-eligible 
                         SKAdNetwork users will be billed for using MMP probabilistic attribution.`
                    }/>
                </DocsPageContent>

                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: Will we still be required to use an MMP if we want to only leverage SKAdNetwork for attribution and billing?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={
                        ` Yes, currently we will require an MMP and the typical click and impression tracking links
                         to be set. It’s expected that the MMP will also handle SKAdNetwork conversion value updating
                          and mapping, as well as forwarding back to us the mapped event values.`
                    }/>
                </DocsPageContent>

                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: How is budgeting handled once the migration to SKAdNetwork billing has taken place?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={
                        ` We will continue to bill on a CPI basis and budgeting will remain in real-time, based on 
                        accumulated spend against your budget value. Due to the delayed nature of SKAdNetwork attribution,
                         it’s possible that latent installs may account for a larger amount of over-budget spend.`
                    }/>
                </DocsPageContent>

                <DocsPageContentSpace withSpace={IndentWidthType.SMALL}/>

                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_1}
                    text={'Changes in data granularity'}
                />
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: What changes to data availability can we expect with the shift to SKAdNetwork?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={` As of `} />
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `May 19th 2021`
                        }
                    />
                    <DocsPageContentText text={
                        `, look for the `
                    }/>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `Probabilistic Simulator`
                        }
                    />
                    <DocsPageContentText text={
                        ` toggle in your dashboard. When set to `
                    }/>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            ` off`
                        }
                    />
                    <DocsPageContentText text={
                        `, installs, spend, and related calculated metrics will be from SKAdNetwork attribution.`
                    }/>
                </DocsPageContent>
                <DocsPageContent>
                    <DocsPageContentText text={`Due to the limitations of SKAdNetwork, this view will be missing:`}/>
                    <DocsPageContentSpace withSpace={IndentWidthType.XSMALL}/>
                    <DocsPageContent
                        text={'•'}
                        withIndent={IndentWidthType.SMALL}
                    >
                        <DocsPageContentText text={`All post-install event metrics`}/>
                    </DocsPageContent>
                    <DocsPageContent
                        text={'•'}
                        withIndent={IndentWidthType.SMALL}
                    >
                        <DocsPageContentText text={`ROAS-related metrics`}/>
                    </DocsPageContent>
                    <DocsPageContent
                        text={'•'}
                        withIndent={IndentWidthType.SMALL}
                    >
                        <DocsPageContentText text={`Ad creative metrics`}/>
                    </DocsPageContent>
                    <DocsPageContent
                        text={'•'}
                        withIndent={IndentWidthType.SMALL}
                    >
                        <DocsPageContentText text={`Most publisher app sources data`}/>
                    </DocsPageContent>
                </DocsPageContent>
                <DocsPageContent>
                    <DocsPageContentText text={
                        `When the simulator is set to `
                    }
                    />
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `on`
                        }
                    />
                    <DocsPageContentText text={
                        `, all data will be sourced from probabilistic attribution,
                         as determined by the MMP, and then sent to us from your MMP. In this view, metrics and data granularity
                          will remain the same as they are today. `
                    }
                    />
                </DocsPageContent>
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: Will there be any restrictions for how many campaigns I can run, per package name?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={
                        ` Yes, due to the limitations of SKAdNetwork, you will be able to only run a single campaign 
                        per campaign type. Campaign types include:`
                    }/>
                    <DocsPageContentSpace withSpace={IndentWidthType.XSMALL}/>
                    <DocsPageContent
                        text={'1.'}
                        withIndent={IndentWidthType.SMALL}
                    >
                        <DocsPageContentText text={`CPI campaign`}/>
                    </DocsPageContent>
                    <DocsPageContent
                        text={'2.'}
                        withIndent={IndentWidthType.SMALL}
                    >
                        <DocsPageContentText text={`CPE (cost per event) campaign and includes CPP 
                        (i.e., The optimized event is conversion of a purchaser.)`}/>
                    </DocsPageContent>
                    <DocsPageContent
                        text={'3.'}
                        withIndent={IndentWidthType.SMALL}
                    >
                        <DocsPageContentText text={`Ad-Based ROAS campaign`}/>
                    </DocsPageContent>
                    <DocsPageContent
                        text={'4.'}
                        withIndent={IndentWidthType.SMALL}
                    >
                        <DocsPageContentText text={`IAP-Based ROAS campaign (only 1 of D0 or D7)`}/>
                    </DocsPageContent>
                </DocsPageContent>

                <DocsPageContentSpace withSpace={IndentWidthType.SMALL}/>

                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_1}
                    text={'Details about your campaigns'}
                />
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: Will I need to create new SKAdNetwork compliant campaigns?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={
                        ` No, your existing iOS campaigns will be compliant. Although, 
                        restriction of one per campaign type defined above will impact 
                        SKAdNetwork attribution beginning during May.`
                    }/>
                </DocsPageContent>
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={`Q: After the migration, what happens to my existing campaigns if I am running 
                    more than one specific campaign type per package name?`}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={
                        ` Our system will automatically attribute all SKAdNetwork installs received. 
                        If you have  multiple  campaigns of a given type, installs for that package name
                         will be attributed to the most recently created one, with precedence given to
                          live over paused campaigns.`
                    }/>
                </DocsPageContent>
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: Will I be able to define how the 100 unique campaign ID values are used for my own attribution purposes?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={
                        `  No, these IDs will be leveraged by our system automatically in order to permit for 
                        an appropriate assessment of cost on SKAdNetwork-attributed installs.`
                    }/>
                </DocsPageContent>

                <DocsPageContentSpace withSpace={IndentWidthType.SMALL}/>

                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_1}
                    text={'Creative testing'}
                />
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: How will creative testing be handled going forward?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={
                        ` Our system will automatically sample and determine the highest performing creatives to serve,
                         just as it does today. Upload your top creatives and allow our system to automatically 
                         choose the best concepts.`
                    }/>
                    <DocsPageContent>
                        <DocsPageContentText text={
                            `You should still regularly test refreshed creative concepts to avoid creative fatigue or take advantage of `
                        }/>
                        <DocsPageContentAnchor
                            text={`seasonally relevant concepts`}
                            to={`https://www.gamesindustry.biz/articles/2020-12-09-are-seasonal-creatives-worth-it`}
                        />
                        <DocsPageContentText text={
                            `(i.e., winter-themed, holiday-themed). If you need to see the breakdown of install 
                            and post-install performance metrics by creative concept, toggle on the `
                        }/>
                        <DocsPageContentText
                            fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                            text={
                                `Probabilistic Simulator`
                            }
                        />
                        <DocsPageContentText text={
                            ` view.`
                        }/>
                    </DocsPageContent>
                </DocsPageContent>

                <DocsPageContentSpace withSpace={IndentWidthType.SMALL}/>

                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_1}
                    text={'Clarity around optimization and reporting'}
                />
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: What campaign targeting options will you have available?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText
                        text=
                            {` All targeting options will still be available and fully functional 
                               as they are today, with the exception of user-specific targeting 
                               (i.e., Custom Audiences and Device ID Targeting). This will function 
                               on the limited subset of IDFA opted-in users on iOS 14+.`
                            }/>
                </DocsPageContent>
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: Will post-install optimized campaign types continue to function?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={` Yes, as long as `}/>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `probabilistic attribution`
                        }
                    />
                    <DocsPageContentText text={` is enabled via your MMP, you will be able to leverage
                     CPE/CPP and IAP-based ROAS campaigns the same as you do today. 
                     Ad-based ROAS campaigns will continue to require an active `}/>
                    <DocsPageContentAnchor
                        text={`MAX mediation`}
                        to={`https://www.applovin.com/max/`}
                    />
                    <DocsPageContentText text={` integration to use.`}/>
                </DocsPageContent>
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: What kind of updates are there for your reporting APIs?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={` Reporting APIs will mirror the `}/>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `probabilistic simulation`
                        }
                    />
                    <DocsPageContentText text={` view when it’s turned off. 
                    This means it will become a lot more limited as only data from 
                    SKAdNetwork attribution will be included.`}/>
                </DocsPageContent>
                <DocsPageHeader
                    type={DocsPageHeaderType.HEADING_2}
                    text={'Q: Will we be able to receive SKAdNetwork postback forwarding directly from you?'}
                />
                <DocsPageContent>
                    <DocsPageContentText
                        fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}
                        text={
                            `A:`
                        }
                    />
                    <DocsPageContentText text={` We do not have plans to forward SKAdNetwork postbacks to advertisers. 
                    We will automatically forward these postbacks to all supported MMPs.`}/>
                </DocsPageContent>

                <DocsPageContent>
                    <DocsPageContentText text={`AppLovin wants to ensure you’re set up for success. 
                    If you have any questions or concerns, please don’t hesitate to contact your account team or `}
                    />
                    <DocsPageContentAnchor
                        text={`AppLovin Support`}
                        to={`https://support.applovin.com/`}
                    />
                </DocsPageContent>

            </ContentWrapper>
        </MainWrapper>
    </>;
};

const LeftSideMenuContainerWrapper = styled.nav`
  width: 260px;

  display: flex;
  flex-direction: column;
  z-index: 10;

  transition-delay: 0.15s;
  transition: width 0.33s linear;

  position: fixed;

  overflow: hidden;

  box-shadow: inset -8px 0 2px -6px #DDD;
  background-color: #F1F4F5;
`;

const Logo = styled( Image )`
  padding: 5px 0 5px 15px;
  background-color: #232323;  
`;

const MainWrapper = styled.main`
  padding-left: 260px;
  flex-grow: 1;
`;

const ContentWrapper = styled.div`
  padding: 100px 0px 40px 75px;
  width: 1000px;
`;

export default FAQComponent;
